import React, { useEffect, useState } from "react";
import { CalendarDays, Power, RotateCcw, Sparkles, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function SeasonalEventsPanel() {
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/events/all").then((r) => setEvents(r.data?.events || [])).catch(() => {});
    api.get("/events/active").then((r) => setActive(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = async (id, label) => {
    setBusy(true);
    try {
      await api.post("/events/set", { id });
      toast.success(label);
      load();
    } catch (e) {
      toast.error("Couldn't update the event.");
    } finally { setBusy(false); }
  };

  const activeId = active?.active ? active.id : null;
  const source = active?.active ? active.source : (active && active.active === false ? "off" : null);

  return (
    <div data-testid="sa-events-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> Seasonal World Events</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Wash a limited-time weather + boss theme across the whole arcade.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => set("", "Back to the auto seasonal calendar")} data-testid="event-set-auto">
            <RotateCcw className="w-4 h-4" /> Auto (by date)
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => set("off", "Seasonal events turned off")} data-testid="event-set-off">
            <Power className="w-4 h-4" /> Turn off
          </Button>
        </div>
      </div>

      <div className="mb-4 text-sm flex items-center gap-2 flex-wrap" data-testid="event-current-status">
        <CalendarDays className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
        {active?.active
          ? <>Live now: <b style={{ color: active.accent }}>{active.emoji} {active.name}</b> <span className="text-xs text-[hsl(var(--muted-foreground))]">({source === "override" ? "manual override" : "auto by date"})</span></>
          : <>Seasonal events are currently <b>off</b>.</>}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {events.map((e) => {
          const isLive = e.id === activeId;
          return (
            <div
              key={e.id}
              data-testid={`event-card-${e.id}`}
              className="rounded-2xl border p-4 flex items-start gap-3"
              style={{ borderColor: isLive ? e.accent : "hsl(var(--border))", background: isLive ? `${e.accent}12` : "transparent" }}
            >
              <span className="shrink-0 text-3xl">{e.emoji}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-bold" style={{ color: e.accent }}>{e.name}</h4>
                  {isLive && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${e.accent}33`, color: e.accent }}>Live</span>}
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))]">weather: {e.weather}</span>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{e.tagline}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" disabled={busy || isLive} onClick={() => set(e.id, `${e.name} is now live across the arcade!`)} data-testid={`event-set-${e.id}`}>
                    {isLive ? "Live now" : "Set live"}
                  </Button>
                  <a href="/app/play" target="_blank" rel="noreferrer" className="text-xs font-semibold text-[hsl(var(--primary))] inline-flex items-center gap-1 hover:underline" data-testid={`event-preview-${e.id}`}>
                    Preview <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
